import { Button, ScrollArea, CopyButton, ActionIcon, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconCopy, IconCheck } from '@tabler/icons-react';
import { truncate } from '../../common/string/truncate';
import { cn } from '../../common/string/classname';

export function TruncatedText({
  text,
  maxLength = 100,
  className = '',
  modalTitle = 'Full Content',
  displayAsCode = false,
  showCopyButton = false,
}: {
  text: string;
  maxLength?: number;
  className?: string;
  modalTitle?: string;
  /** Whether to render as code block */
  displayAsCode?: boolean;
  /** Whether to show copy button on hover */
  showCopyButton?: boolean;
}) {
  const shouldTruncate = text.length > maxLength;
  const displayText = truncate(text, maxLength);

  const openFullTextModal = () => {
    modals.open({
      title: modalTitle,
      size: 'lg',
      centered: true,
      children: (
        <div style={{ height: '60vh' }}>
          <div className="mb-3 flex justify-end">
            <CopyButton value={text}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy'}>
                  <ActionIcon
                    color={copied ? 'teal' : 'gray'}
                    variant="subtle"
                    onClick={copy}
                  >
                    {copied ?
                      <IconCheck size={16} />
                    : <IconCopy size={16} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </div>
          <ScrollArea h="calc(100% - 40px)">
            {displayAsCode ?
              <pre className="whitespace-pre-wrap rounded bg-gray-100 p-3 font-mono text-sm text-gray-800 dark:bg-neutral-800 dark:text-gray-200">
                {text}
              </pre>
            : <div className="whitespace-pre-wrap break-words text-sm">{text}</div>}
          </ScrollArea>
        </div>
      ),
    });
  };

  return (
    <div className={cn(showCopyButton ? 'group flex flex-col gap-1' : 'flex flex-col gap-1', className)}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          {displayAsCode ?
            <code className="break-all rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-800 dark:bg-neutral-800 dark:text-gray-200">
              {displayText}
            </code>
          : <span className="break-all text-sm text-gray-800 dark:text-gray-200">{displayText}</span>}
        </div>
        {!!showCopyButton && (
          <CopyButton value={text}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Copied' : 'Copy'}>
                <ActionIcon
                  size="sm"
                  color={copied ? 'teal' : 'gray'}
                  variant="subtle"
                  onClick={copy}
                  className="opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                >
                  {copied ?
                    <IconCheck size={14} />
                  : <IconCopy size={14} />}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        )}
      </div>
      {!!shouldTruncate && (
        <Button
          variant="subtle"
          size="compact-xs"
          onClick={openFullTextModal}
          className="self-start text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          see more
        </Button>
      )}
    </div>
  );
}
